import { Button } from '@/components/ui/button';
import { MenuContent, MenuRoot, MenuTrigger } from '@/components/ui/menu';
import { FiChevronDown, FiCopy } from 'react-icons/fi';
import {
  AccordionItem,
  HStack,
  Icon,
  Text,
  VStack,
  Image,
} from '@chakra-ui/react';
import {
  AccordionItemContent,
  AccordionItemTrigger,
  AccordionRoot,
} from '@/components/ui/accordion';
import { CiWallet } from 'react-icons/ci';
import { useTranslations } from 'next-intl';
import { requestSetWalletInfo } from '@/lib/services/wallet.service';
import { CHAIN_INFO, CHAIN_ID_MAP } from '@/lib/config/chain';
import { toaster } from '../ui/toaster';
import {
  UserWallet,
  UserWalletGroup,
  useUserCurrencyList,
} from '@/lib/services/user.service';

export function UserWalletMenu() {
  const { data: userCurrencyList, refetch } = useUserCurrencyList();
  const walletsList = userCurrencyList?.userWalletGroups || [];
  const defaultWallet = userCurrencyList?.userWalletGroups.filter(
    (item) => item.isUsing,
  )[0];
  const tAddress = useTranslations('TradePage.TokenDetail');

  const handleCopyAddress = (address: string) => {
    navigator.clipboard.writeText(address);
    //toast message
    toaster.create({
      title: tAddress('addressCopied'),
      type: 'success',
    });
  };

  const t = useTranslations('Navigation.userWalletMenu');

  const onSetDefaultWallet = async (data: UserWalletGroup) => {
    try {
      await requestSetWalletInfo({
        wallet_group_id: data?.walletGroupId,
      });
      refetch();
    } catch (error: any) {
      toaster.create({
        title: error.message || t('select_account_failed'),
        type: 'error',
      });
    }
  };

  return (
    <MenuRoot>
      <MenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          bg="bg.t2"
          borderRadius="100px"
          px={4}
          py={2}
          borderColor="text.t4"
          _hover={{
            bg: 'bg.t1',
          }}
        >
          <HStack gap={2}>
            <Icon fontSize={20}>
              <CiWallet />
            </Icon>
            <Text color="text.t1" fontSize="sm" fontWeight="medium">
              {t('wallet')} {defaultWallet?.walletGroupId}
            </Text>
            <Text color="text.t2" fontSize="sm">
              $
              {defaultWallet?.wallGroupValues
                ? Number(defaultWallet?.wallGroupValues).toFixed(2)
                : '0.00'}
            </Text>
            <FiChevronDown color="text.t3" size={16} />
          </HStack>
        </Button>
      </MenuTrigger>

      <MenuContent
        width={320}
        padding={4}
        gap={4}
        borderRadius="12px"
        bg="bg.t1"
        backdropFilter="blur(20px)"
      >
        <AccordionRoot>
          {walletsList.map((item: UserWalletGroup, index) => (
            <AccordionItem key={index} value={item.walletGroupId.toString()}>
              <AccordionItemTrigger>
                <HStack
                  h={'44px'}
                  cursor={'pointer'}
                  onClick={() => {
                    onSetDefaultWallet(item);
                  }}
                >
                  <Icon>
                    <CiWallet />
                  </Icon>
                  <Text>
                    {t('wallet')} {item.walletGroupId}
                  </Text>
                </HStack>
              </AccordionItemTrigger>
              <AccordionItemContent maxH={'400px'}>
                {item?.userWallets.map((json: UserWallet, key: number) => (
                  <HStack
                    mt={3}
                    borderRadius="8px"
                    bg="bg.t2"
                    w="full"
                    justify="space-between"
                    align="stretch"
                    gap={3}
                    p={2}
                    key={key}
                  >
                    <HStack justify="space-between">
                      <Image
                        src={
                          CHAIN_INFO[CHAIN_ID_MAP[json.chainId!.toString()]]
                            .icon
                        }
                        rounded={'full'}
                        w={'18px'}
                        h={'18px'}
                      />
                      <VStack gap={0}>
                        <Text fontSize="sm">
                          {
                            CHAIN_INFO[CHAIN_ID_MAP[json.chainId!.toString()]]
                              .name
                          }
                        </Text>
                        <Text fontSize="sm" color="text.t2">
                          ${json?.tokenValue?.toFixed(2)}
                        </Text>
                      </VStack>
                    </HStack>
                    <HStack justify="space-between">
                      <HStack gap={2}>
                        <Text fontSize="sm" color="text.t2" fontWeight="medium">
                          {`${json?.userWalletAddress?.slice(0, 4)}...${json?.userWalletAddress?.slice(-4)}`}
                        </Text>
                      </HStack>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          handleCopyAddress(json?.userWalletAddress || '');
                        }}
                      >
                        <FiCopy />
                      </Button>
                    </HStack>
                  </HStack>
                ))}
              </AccordionItemContent>
            </AccordionItem>
          ))}
        </AccordionRoot>
      </MenuContent>
    </MenuRoot>
  );
}
